import { useMemo, useState } from 'react';
import {
  FieldLabel,
  InfoSection,
  PanelTitle,
  ResultsEmpty,
  ToolPageHeading,
  ToolTwoColumn,
  fmtUsd,
  monthlyMortgagePayment,
  parseNum,
  toolInputClass,
} from './toolShared';

const DEFAULTS = { principal: '', rate: '', years: '30' };

export function ToolMortgage() {
  const [v, setV] = useState(DEFAULTS);

  const payment = useMemo(() => {
    const p = parseNum(v.principal);
    const r = parseNum(v.rate);
    const y = parseNum(v.years);
    if (p <= 0 || y <= 0) return null;
    return monthlyMortgagePayment(p, r, y);
  }, [v]);

  return (
    <div className="space-y-8">
      <ToolPageHeading
        title="Mortgage calculator"
        subtitle="Calculate monthly mortgage payments for a fixed-rate amortizing loan."
      />

      <InfoSection title="Understanding your mortgage">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Loan amount</strong> — Principal borrowed (after down
            payment).
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Interest rate</strong> — Annual nominal rate (APR basis
            simplified here).
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Loan term</strong> — Amortization period in years (e.g.
            30).
          </li>
        </ul>
      </InfoSection>

      <ToolTwoColumn
        onReset={() => setV(DEFAULTS)}
        left={
          <>
            <PanelTitle>Loan details</PanelTitle>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="mtg-p" tip="Total loan principal." required>
                  Loan amount
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="mtg-p"
                    type="text"
                    inputMode="decimal"
                    value={v.principal}
                    onChange={(e) => setV((s) => ({ ...s, principal: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="mtg-r" tip="Annual interest rate (%)." required>
                  Interest rate
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="mtg-r"
                    type="text"
                    inputMode="decimal"
                    value={v.rate}
                    onChange={(e) => setV((s) => ({ ...s, rate: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="mtg-y" tip="Amortization term in years." required>
                  Loan term (years)
                </FieldLabel>
                <input
                  id="mtg-y"
                  type="text"
                  inputMode="numeric"
                  value={v.years}
                  onChange={(e) => setV((s) => ({ ...s, years: e.target.value }))}
                  className={toolInputClass}
                  placeholder="30"
                />
              </div>
            </div>
          </>
        }
        right={
          <>
            <PanelTitle>Mortgage summary</PanelTitle>
            {payment === null ? (
              <ResultsEmpty message="Enter loan details to calculate mortgage payment." />
            ) : (
              <div className="flex min-h-[220px] flex-col justify-center space-y-2">
                <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                  Monthly P&amp;I
                </p>
                <p className="font-display text-3xl font-bold text-[var(--home-text-on-dark)]">{fmtUsd(payment)}</p>
              </div>
            )}
          </>
        }
      />
    </div>
  );
}
